/** Plain-English tooltips and section subtitles for Weld Analytics. */

import { QUALITY_THRESHOLDS } from './chartHelpers';

function qualityScoreFormulaHelp(): string {
  const { acceptable, warning } = QUALITY_THRESHOLDS;
  return (
    `Weld Quality = 100 − anomaly_pct (from WeldIQ), or (1 − composite_score) × 100 when anomaly_pct is unavailable. ` +
    `Scores range 0–100; higher is better. Good: ≥ ${acceptable}. Warning: ${warning}–${acceptable - 1}. Critical: below ${warning}.`
  );
}

const QUALITY_SCORE_HELP = qualityScoreFormulaHelp();

export const WELD_FIELD_GLOSSARY: Record<string, string> = {

  // —— Section subtitles (visible helper text, not tooltips) ——

  live_scoring_subtitle:
    'Scores for this weld, calculated when you open it or click Refresh.',

  anomaly_metrics_subtitle:

    'Compares ML anomaly flags with WPS failures. Indicative overlap only — not certified model accuracy.',



  // —— Weld summary table ——

  weld_rank:

    'Attention rank across all welds in this run. Rank 1 needs the most review. Sorting this table does not change the rank.',

  display_rank: 'Row order in this table after your current sort and filters.',

  weld_number: 'Weld or joint number from production records.',

  job_number: 'Job or WPS number this weld belongs to.',

  welding_system: 'Welding machine or system that made this weld.',

  composite_score:

    'How unusual this weld looks overall. Lower values need more attention. Not a percentage — compare welds on the same scale.',

  total_passes: 'Torch passes recorded for this weld.',

  failed_passes: 'Passes that exceeded WPS limits.',

  anomalous_passes: 'Passes flagged as unusual by WeldIQ.',

  anomaly_pct: 'Share of passes flagged as unusual.',

  review_priority:

    'How urgently to review this weld. Critical and High need attention first. Based on heat limit failures and unusual welding patterns.',

  open_weld_drilldown: 'Open detail for this weld.',

  parameter: 'WPS setting being checked (for example heat input or travel speed).',

  checked: 'Readings checked against WPS limits.',

  compliant: 'Readings within WPS limits.',

  non_compliant: 'Readings outside WPS limits.',

  compliance_rate_pct: 'Share of readings that stayed within WPS limits.',

  overall_compliance_rate_pct: 'Overall WPS pass rate for the selected job(s).',

  total_passes_compliance: 'Passes included in the compliance calculation.',



  // —— Insights ——

  priority: 'Insight urgency: HIGH = act now, MEDIUM = plan a review, LOW = for awareness.',

  confidence: 'How strongly the pattern appears in the data (0–100%).',

  category: 'Type of pattern found (for example quality or equipment).',

  recommendation: 'Suggested next step based on this finding.',

  insight_title: 'Short summary of what was found.',

  insight_body: 'More detail on what the data shows.',

  insight_why: 'Why this insight was raised.',



  // —— Live scoring ——

  live_scoring:

    'On-demand WeldIQ scores for this weld. Open drill-down or click Refresh to recalculate.',

  live_pass_for_indication:

    'Each weld has many torch passes. Choose which pass to check for likely defect types.',

  live_indication_scope:

    'Choose entire weld to aggregate defect signals across all passes, or pick one pass for a focused check. Entire weld uses stored pass data; a single pass can use live temperature logs when available.',

  live_indication_pass_label_hint:

    'Each option is one torch pass. Lead/Trail = torch side, Station = welding station, (2nd) = second pass with the same name.',

  live_anomaly_check_card:

    'Anomaly identified — unusual patterns, heat limit breaches, and overall risk for this weld.',

  live_indication_check_card:

    'Indication identified — most likely defect type for the selected scope. Needs enough temperature log data.',

  live_reject_prediction_card:
    'Rejection confirmed — estimated inspection rejection risk from the same live weld score.',

  live_reject_probability:

    'Estimated rejection chance (0–1). Values at or above 0.25 are treated as high risk.',

  live_anomaly_confidence:

    'Strength of the unusual-pattern signal (0–100%). Based on the strongest pass on this weld.',

  live_indication_confidence:

    'Confidence in the defect call for the selected pass (0–100%). Read together with Data sufficiency.',

  live_top_indication:

    'Most likely defect type on the selected pass. “None” means no single type stood out clearly.',

  live_data_sufficiency:

    'Whether temperature logs support a reliable indication: sufficient (20+ readings), limited (5–19), or insufficient (fewer than 5).',

  live_predicted_reject:

    'Whether the reject model flags this weld. True means reject probability is at or above 0.25.',

  live_if_anomalous_passes:

    'Passes that looked unusual compared to normal welding patterns (for example 1 / 18 = one unusual pass out of eighteen).',

  live_heat_failed_passes:

    'Passes that exceeded heat-input WPS limits (for example 0 / 18 = none failed).',



  // Aliases — same meaning in live cards and elsewhere

  live_review_priority:

    'How urgently to review this weld. Critical and High need attention first. Based on heat limit failures and unusual welding patterns.',

  live_weld_composite_score:

    'How unusual this weld looks overall. Lower values need more attention. Not a percentage — compare welds on the same scale.',

  drilldown_priority:

    'How urgently to review this weld. Critical and High need attention first. Based on heat limit failures and unusual welding patterns.',



  // —— Anomaly metrics ——

  total_welds: 'Total passes scored for the selected job(s).',

  num_rule_based: 'Passes that failed official WPS checks.',

  num_per_system: 'Passes flagged as unusual by the ML model for that welding system.',

  num_global: 'Passes flagged by the ML model trained across all systems.',

  num_rule_and_per_system: 'Passes where both WPS failure and ML agree something is wrong.',

  num_only_per_system:

    'Possible hidden issues: flagged by ML but did not fail WPS — worth a closer look.',

  num_only_rule_based: 'WPS failures that the ML model did not flag.',

  pass_proxy_tp: 'Passes where both WPS and ML flagged a problem.',

  pass_proxy_fp: 'Passes flagged by ML only — WPS did not fail.',

  pass_proxy_fn: 'Passes that failed WPS but were not flagged by ML.',

  pass_proxy_precision_pct: 'Of ML-flagged passes, how many also had a WPS failure.',

  pass_proxy_recall_pct: 'Of WPS-failed passes, how many ML also flagged.',

  joint_proxy_tp: 'Welds where both WPS and ML flagged a problem on at least one pass.',

  joint_proxy_fp: 'Welds flagged by ML with no WPS failure on any pass.',

  joint_proxy_fn: 'Welds with a WPS failure but no ML flag.',

  joint_proxy_precision_pct: 'Of ML-flagged welds, how many also had a WPS failure.',

  joint_proxy_recall_pct: 'Of welds with a WPS failure, how many ML also flagged.',

  anomaly_overlap_rule_only: 'WPS failures the ML model did not catch.',

  anomaly_overlap_both: 'Issues where WPS failure and ML both agree.',

  anomaly_overlap_ml_only: 'ML-only flags — possible issues WPS limits did not catch.',

  anomaly_proxy_level: 'Whether the comparison is at pass level or weld (joint) level.',

  anomaly_proxy_tp: 'Both WPS and ML flagged a problem.',

  anomaly_proxy_fp: 'ML flagged it, but WPS did not fail.',

  anomaly_proxy_fn: 'WPS failed, but ML did not flag it.',

  anomaly_proxy_precision: 'Of ML flags, how many also had a WPS failure.',

  anomaly_proxy_recall: 'Of WPS failures, how many ML also flagged.',



  // —— Overview dashboard KPIs ——

  overview_total_welds:

    'Total welds in the selected job(s) with production data for the current filter.',

  welds_within_wps_limit:

    'Welds whose production status is within WPS limits for the selected calculation method.',

  welds_out_of_wps_limit:

    'Welds whose production status is outside WPS limits for the selected calculation method.',

  wps_limit_compliance_rate:

    'Percentage of welds within WPS limit in the selected job(s). Click to drill down.',

  avg_weld_duration: 'Average time to complete one weld, from production records.',

  fastest_weld: 'Shortest weld duration in the selected data.',

  slowest_weld: 'Longest weld duration in the selected data.',

  avg_quality_score:

    `Average of per-weld quality scores in the current selection. ${QUALITY_SCORE_HELP}`,

  weld_quality_score: QUALITY_SCORE_HELP,



  // —— Overview dashboard charts ——

  within_vs_out_limit_chart:

    'Share of welds within vs out of WPS limit. Center shows the compliance rate.',

  weld_duration_analysis:

    'Duration timeline with target band. Hover a point for weld context; click to drill down.',

  weld_quality_trend:

    `Quality score trend with threshold zones — hover for context; click a point to drill down. ${QUALITY_SCORE_HELP} The chart shows each weld score (solid line), a 5-weld rolling average (dashed), and markers on abnormal welds.`,

  performance_distribution:

    'How many welds fall into each duration range. Click a bar to list those welds.',

  wps_limit_timeline:

    'One row per pipe size. Each rounded segment is one weld in sequence — green within limit, red out of limit, grey unknown. Hover for details; click a segment to drill down.',

  production_summary:

    'Session-level productivity: total time, idle gaps, and welds per hour.',



  // —— NDT & outcomes ——

  ndt_indication: 'NDT indication or repair log entry for this weld.',

  ndt_evaluation: 'Inspector evaluation (for example Accepted or Rejected).',

  ndt_defect_type: 'Defect type recorded during NDT inspection.',

  ndt_welder: 'Welder number(s) linked to this NDT indication.',

  weld_outcome_within: 'Weld production status is within WPS limits.',

  weld_outcome_out: 'Weld production status is outside WPS limits.',



  // —— Drill-down ——

  drilldown_sl_no: 'Serial number in the current filtered and paginated weld list.',

  drilldown_start_end:
    'Clock time from production start to production end. This span can be longer than Weld Duration when welding was not continuous.',

  drilldown_duration:
    'Recorded welding time from production — how long the arc was on. Start → End is the clock span and is longer when there is idle time between passes.',

  drilldown_ndt_badge: 'Number of NDT indication log entries for this weld.',

  drilldown_welder_id: 'Welder ID(s) from production pass records for this weld.',

  drilldown_total_ndt_logs: 'Total NDT indication log entries across all welds in this modal.',

  drilldown_job_pill: 'Job number(s) in the current filtered weld set.',

  drilldown_wps_pill: 'WPS number(s) in the current filtered weld set.',

  drilldown_pipe: 'Pipe diameter and wall thickness from production records.',

  drilldown_wps_number: 'Welding Procedure Specification (WPS) number used for this weld.',

  wps_status_active:

    'Overall WPS result using the project’s chosen check method. Passed = within limits; Out of limit = outside WPS limits.',

  wps_status_instantaneous:

    'Result from checking live welding readings against WPS limits.',

  wps_status_avg_zone:

    'Result from checking average values in each weld zone against WPS limits.',

  wps_status_avg_pass:

    'Result from checking average values for each torch pass against WPS limits.',

  wps_status_avg_tilt:

    'Result from checking average values in each tilt/position band against WPS limits.',

  ndt_date: 'Date the NDT indication was recorded.',

  ndt_location: 'Physical location of the indication on the weld.',

  ndt_repair: 'Repair action taken for this indication, if any.',

  ndt_qc_welds_inspected:

    'Welds that have at least one NDT indication log in the loaded data.',

  ndt_qc_acceptance_rate:

    'Percentage of inspected welds with an accepted NDT evaluation.',

  ndt_qc_rejected_welds: 'Welds with at least one rejected NDT outcome.',

  ndt_qc_defect_type_distribution:

    'How often each defect type (for example porosity or crack) appears in NDT logs.',

  ndt_qc_acceptance_rejection_split:

    'Share of individual NDT log entries marked accepted vs rejected — log-level, not weld-level.',

  ndt_qc_compact_strip:

    'Quick NDT snapshot: inspection coverage, acceptance, rejections, and the most common defect type.',

  drilldown_view_toggle:

    'Switch between browsing individual welds and viewing aggregated analytics for the full list.',

  drilldown_individual_view:

    'Browse welds one at a time — select a row to see production, WPS status, anomalies, and NDT logs.',

  drilldown_summary_view:

    'Aggregated analytics for all welds in this list: WPS breach patterns and NDT at a glance.',

  wps_violation_summary:

    'WPS breach analytics for failed welds — parameters, locations, and compliance over weld sequence.',

  wps_all_welds_top_failed_parameter:

    'WPS parameter with the most violation locations across failed welds (for example heat input).',

  wps_all_welds_pass_breaches:

    'Total pass-, zone-, and tilt-level WPS violation records across failed welds.',

  wps_all_welds_welders_affected:

    'Unique welder IDs associated with failed welds in this list.',

  wps_all_welds_pareto:

    'Bars show violation counts per parameter; the line shows cumulative share. Focus on parameters reaching ~80% first.',

  wps_all_welds_compliance_trend:

    'Rolling pass rate along weld sequence with failed-weld count per window.',

  wps_all_welds_failed_table:

    'Failed welds ranked by worst deviation. Expand a row for breach details; click to open individual detail.',

};



export function glossaryText(key: string, fallback?: string): string {

  return WELD_FIELD_GLOSSARY[key] ?? fallback ?? 'No description available.';

}



export function formatMetricLabel(key: string): string {

  return key

    .replace(/_/g, ' ')

    .replace(/\b\w/g, (c) => c.toUpperCase());

}


